import {
  Badge,
  Card,
  cardClasses,
  cn,
  Icon,
  IconTile,
  Page,
  PageHeader,
  type IconName,
} from '@evogard/ui';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@evogard/ui/primitives';
import { Navigate, useNavigate, useSearch } from '@tanstack/react-router';
import { HOUSE_TIMEZONE } from '@evogard/domain';
import {
  HOME_TABS,
  PANELS,
  type HomePanel,
  type HomePanelPlacement,
  type HomeTab,
} from '../../panels.js';
import { can, useSessionContext } from '../../lib/session.js';

/**
 * O início: os painéis que a pessoa pode ver, e o que a plataforma é.
 *
 * A lista fixa de módulos saiu do lugar de destaque e virou rodapé informativo.
 * O que ocupa o topo agora são os **painéis registrados em `panels.ts`**,
 * filtrados por `pode()` — cada módulo publica os próprios números pelo próprio
 * contrato, e nenhum deles edita este arquivo.
 *
 * ## O desenho de 29/09/2026, e o que ficou de fora dele
 *
 * Saudação com a data, a fileira de números das demandas, "Visão comercial" e
 * "Minhas demandas" lado a lado, os outros painéis abaixo e a grade de módulos.
 * O cartão "Suas permissões nesta base" saiu: a lista mora na tela de grupos.
 *
 * A referência trazia duas coisas que **não** entraram, pela regra abaixo: a
 * variação "+20% em relação ao mês anterior" em cada número e a série de seis
 * meses das vendas. `GET /tickets/numbers` e `GET /sales/numbers` respondem
 * só o agora; desenhar as duas seria a tela inventando o passado. Entram quando
 * as rotas as devolverem.
 *
 * **Não fabricar número de módulo que ainda não existe** é a mesma regra de
 * "nenhuma tela afirma o que o código não cumpre", aplicada ao início.
 *
 * ## A regra do `detalhe`, e por que ela precisou ser escrita
 *
 * `detalhe` afirma **só o que é verdade da construção**: o que o módulo tem de
 * pé, e onde se configura o que ele precisa. Nunca o estado do DADO de uma base.
 *
 * Até 29/08 ele misturava as duas coisas — "o quadro nasce sem fase nenhuma nas
 * duas bases", "a régua nasce vazia", "o espelho nunca foi preenchido" — e as
 * frases de dado eram erradas de três maneiras ao mesmo tempo:
 *
 * · **envelheciam sozinhas.** Este arquivo registra duas ocasiões em que isso
 *   aconteceu: a cobrança ficou dizendo "Onda 5" depois de entrar no ar, e o
 *   comercial ficou dizendo que "não há tela que cadastre a régua" quando havia.
 *   Bastou `make db-demo` carregar uma régua para as três voltarem a mentir;
 * · **afirmavam sobre a OUTRA base.** "Nas duas bases" numa tela escopada a uma:
 *   configurar a CE e não a GO já bastava para a frase ficar falsa, sem
 *   demonstração nenhuma no meio;
 * · **eram uma segunda cópia, e a cópia sem dono.** O painel de cada módulo,
 *   três centímetros acima, responde a mesma pergunta **medida e por base**:
 *   `sem_quadro`, `sem_coleta`, `sem_vendas`, `sem_regua`. Quem lê o rodapé
 *   dizendo "a régua nasce vazia" logo abaixo de um painel mostrando as cinco
 *   etapas cheias não conclui "o rodapé está velho": conclui que a tela quebrou.
 *
 * O que **fica** é o ponteiro — "a régua fica em Cobrança → Configuração →
 * Régua de cobrança" é verdade sobre o menu, não sobre o dado, e continua certa
 * com a base cheia ou vazia. Tirá-lo junto seria mandar a pessoa embora do único
 * lugar que destrava a base dela, que é o defeito oposto e igualmente caro.
 *
 * Em uma linha: **se a frase pode ficar falsa por alguém cadastrar um registro,
 * ela não é deste arquivo.** É do painel do módulo, que a mede.
 */
const MODULES: Array<{ nome: string; detail: string; icon: IconName; live: boolean }> = [
  {
    nome: 'Plataforma',
    detail: 'Identidade, permissões, bases e auditoria.',
    icon: 'plataforma',
    live: true,
  },
  {
    nome: 'Ticket',
    detail: 'Abertura, acompanhamento e portal do associado.',
    icon: 'demandas',
    live: true,
  },
  /*
   * O selo é sobre EXISTIR — não sobre estar cheio.
   *
   * Os módulos de negócio estão registrados na API, têm permissão no catálogo,
   * ícone no rail e tela que responde. Negar o selo a um módulo cujo ícone está
   * na coluna ao lado seria esta tela contradizendo a navegação.
   *
   * O que os separa de plataforma e demandas é o DADO — e é justamente por isso
   * que o dado NÃO está escrito aqui: quem responde por ele é o painel de cada
   * módulo, na mesma tela, que o mede na base aberta. Ver o cabeçalho deste
   * arquivo.
   *
   * As frases encurtaram em 29/09/2026, com o desenho novo do início, e o que
   * ficou de cada uma é o PONTEIRO — onde se configura o que o módulo precisa.
   * Ele é verdade sobre o menu, e não sobre o dado.
   */
  {
    nome: 'ERP (espelho do Hinova)',
    detail:
      'Porta única do Hinova e espelho por praça. A sincronização fica em ERP → Saúde da integração.',
    icon: 'erp',
    live: true,
  },
  {
    nome: 'Retenção',
    detail:
      'Do pedido de cancelamento ao desfecho. Fases e motivos em Retenção → Configuração.',
    icon: 'retencao',
    live: true,
  },
  {
    nome: 'Cobrança',
    detail:
      'Mesa, esteira e boleto agnóstico de provedor. A régua fica em Cobrança → Configuração.',
    icon: 'cobranca',
    live: true,
  },
  /*
   * **A frase do ranking saiu em 09/09, e a saída é a regra deste arquivo em
   * ação.** O que o ranking ainda recusa é dado de cadastro — placa sem
   * categoria —, e ressalva que sai por alguém
   * cadastrar algo não pertence a este arquivo.
   */
  {
    nome: 'Comercial',
    detail:
      'Vendas, metas, ranking, comissões e cadastro. A régua da comissão fica em Comercial → Configuração.',
    icon: 'comercial',
    live: true,
  },
  /*
   * "Onda 7" e sem selo até 29/09/2026 — e a gestão já tinha o Painel de
   * Gestão, a carteira e o extrato no menu do comercial, com a premiação paga
   * como despesa do Financeiro desde 24/09. Era a tela contradizendo a
   * navegação, que é o que o selo existe para não fazer.
   */
  {
    nome: 'Gestão e premiação',
    detail: 'Painel de gestão, carteira, extrato e premiação, no menu do comercial.',
    icon: 'gestao',
    live: true,
  },
];

/**
 * Os painéis de UMA aba, nas três faixas — e quem põe cada painel numa delas
 * é o `panels.ts`.
 *
 * Os números primeiro, porque são o que se lê de manhã; depois os cartões lado
 * a lado; depois o resto, empilhado. `gap-6` e não `gap-4`: são seções da
 * página, e o ritmo entre seções é 24/32 no `DESIGN.md` — com 16px as fileiras
 * de dois painéis liam como uma só.
 */
function TabPanels({ tab, visible }: { tab: HomeTab; visible: HomePanel[] }) {
  const mine = visible.filter((p) => p.tab === tab);
  const at = (placement: HomePanelPlacement) =>
    mine.filter((p) => p.placement === placement);
  const columns = at('column');

  return (
    <div className="flex flex-col gap-6">
      {at('numbers').map(({ key, component: Panel }) => (
        <Panel key={key} />
      ))}

      {columns.length > 0 && (
        /*
         * Duas colunas só quando há dois cartões: um sozinho em meia largura
         * deixaria metade da fileira em branco para quem não pode ver o outro.
         */
        <div
          className={cn(
            'grid grid-cols-1 items-stretch gap-4',
            columns.length > 1 && 'lg:grid-cols-2',
          )}
        >
          {columns.map(({ key, component: Panel }) => (
            <Panel key={key} />
          ))}
        </div>
      )}

      {at('strip').map(({ key, component: Panel }) => (
        <Panel key={key} />
      ))}
    </div>
  );
}

/** "Segunda-feira" e "29 de setembro de 2026" — hoje, no fuso da casa. */
function todayParts(): { weekday: string; date: string } {
  const now = new Date();
  const weekday = new Intl.DateTimeFormat('pt-BR', {
    weekday: 'long',
    timeZone: HOUSE_TIMEZONE,
  }).format(now);
  const date = new Intl.DateTimeFormat('pt-BR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: HOUSE_TIMEZONE,
  }).format(now);
  return { weekday: weekday.charAt(0).toUpperCase() + weekday.slice(1), date };
}

export function HomeScreen() {
  const { data: context } = useSessionContext();
  const search = useSearch({ from: '/authenticated/' });
  const navigate = useNavigate();
  if (!context) return null;

  /*
   * O consultor começa em Comercial › Início (28/10/2026, pedido do dono), e o
   * item desta tela some do menu dele (`hiddenForConsultant`). Só redireciona
   * quem pode abrir aquela tela — sem `sales.management:ver` ela recusaria, e
   * a raiz é o lugar seguro para cair.
   */
  if (context.consultant && can(context, 'sales.management', 'ver')) {
    return <Navigate to="/sales/home" replace />;
  }

  const visible = PANELS.filter(
    (p) => !p.resource || can(context, p.resource, p.action ?? 'ver'),
  );
  /* A cascata do menu: aba sem painel visível não aparece. */
  const tabs = HOME_TABS.filter((t) => visible.some((p) => p.tab === t.key));
  /* Pedida no endereço e visível, ou a primeira que a pessoa vê. */
  const current = tabs.find((t) => t.key === search.panel) ?? tabs[0];
  const today = todayParts();

  return (
    <Page measure="cheia">
      <PageHeader
        title={<>Olá, {context.user.nome.split(' ')[0]}</>}
        actions={
          <p
            className={cardClasses({
              density: 'densa',
              className: 'flex items-center gap-2.5 py-2 text-xs',
            })}
          >
            <Icon nome="calendario" className="text-texto-suave" />
            <span>
              <span className="block font-medium text-texto">{today.date}</span>
              <span className="block text-texto-suave">{today.weekday}</span>
            </span>
          </p>
        }
      >
        Você está na base{' '}
        <strong className="font-medium text-texto">{context.currentTenant.nome}</strong> —
        aqui está o resumo dela.
      </PageHeader>

      {/*
       * Uma aba por módulo, desde 01/10/2026 — ver `HOME_TABS`.
       *
       * Com uma aba só não há o que alternar, e a barra não aparece: um rótulo
       * solitário em cima do único painel é moldura sem função. O conteúdo da
       * aba fechada não é montado (o `TabsContent` do Radix desmonta), então a
       * consulta de cada módulo só sai quando alguém abre a aba dele.
       */}
      {current &&
        (tabs.length === 1 ? (
          <TabPanels tab={current.key} visible={visible} />
        ) : (
          <Tabs
            value={current.key}
            onValueChange={(value) =>
              void navigate({
                to: '/',
                search: {
                  panel: value === tabs[0]!.key ? undefined : (value as HomeTab),
                },
                replace: true,
              })
            }
          >
            <TabsList aria-label="Resumo por módulo">
              {tabs.map((t) => (
                <TabsTrigger key={t.key} value={t.key}>
                  {t.label}
                </TabsTrigger>
              ))}
            </TabsList>
            {tabs.map((t) => (
              <TabsContent key={t.key} value={t.key} className="pt-5">
                <TabPanels tab={t.key} visible={visible} />
              </TabsContent>
            ))}
          </Tabs>
        ))}

      <Card
        title={
          <span className="flex items-center gap-2">
            <Icon nome="grade" className="text-texto-suave" />
            Módulos
          </span>
        }
        descricao="O que já existe na plataforma."
      >
        {/*
         * Duas colunas de linhas, e não uma grade de cartões: cartão dentro de
         * cartão desenha duas molduras para a mesma coisa, e a grade de
         * cartões idênticos com ícone, título e parágrafo é o que o
         * `DESIGN.md` bane.
         */}
        <ul className="grid grid-cols-1 gap-x-6 md:grid-cols-2">
          {MODULES.map((m) => (
            <li
              key={m.nome}
              className="flex items-center gap-3 border-b border-borda-suave py-3"
            >
              <IconTile icon={m.icon} />
              <div className="min-w-0 flex-1">
                <p
                  className={m.live ? 'text-sm font-medium' : 'text-sm text-texto-suave'}
                >
                  {m.nome}
                </p>
                <p className="text-xs text-texto-suave">{m.detail}</p>
              </div>
              {/*
               * "no ar" era texto verde e mais nada. Cor sozinha não é estado:
               * quem não distingue verde de cinza lia dois módulos iguais.
               */}
              {m.live && <Badge tone="sucesso">No ar</Badge>}
            </li>
          ))}
        </ul>
      </Card>
    </Page>
  );
}
